import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  feeCompanySubscriptionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { feeEngineEndpoints } from "@/features/fee-engine/api/endpoints";
import type { CompanySubscriptionDto } from "@/features/fee-engine/api/dto";

/**
 * Souscrit une entreprise à un plan d'abonnement
 * (`POST /CompanySubscriptions/add`, guide §6.5).
 */
export const useCreateCompanySubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fee-engine", "company-subscriptions", "create"],
    mutationFn: (body: CompanySubscriptionDto) =>
      mutateData({
        url: feeEngineEndpoints.companySubscriptions.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<CompanySubscriptionDto>,
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        feeCompanySubscriptionCacheKeys(),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateCompanySubscription failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fee-engine" }
      );
    },
  });
};
